import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaArrowLeft,
  FaSave,
  FaSearch,
  FaBoxOpen,
  FaSlidersH,
  FaThLarge,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import ProductApis from '../../apis/product';

// Admin screen: control what a single dealer can see.
// Model: HIDE-LIST, default-visible. Everything is shown unless the admin unchecks it.
//   - "Products" tab: pick which products the dealer sees (all products, no pagination).
//   - "Variations" tab: pick which variation options the dealer sees. Variations are
//     store-wide/unique (shared across products), so a choice here applies everywhere.
// Hiding a variation item also hides anything nested under it on the dealer side.
const PRODUCTS_TAKE = 10000;

const EMPTY_HIDDEN = {
  subcategories: [],
  products: [],
  variationItems: [],
  detailItems: [],
  detailDetailItems: [], // retained for forward-compat; not edited here
};

const ManageProductVisibility = ({ adminApis, adminData, dealer, onBack }) => {
  const productApis = useMemo(
    () => (adminApis ? new ProductApis(adminApis.storeId) : null),
    [adminApis]
  );

  const [activeTab, setActiveTab] = useState('categories');

  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [subcategoriesLoaded, setSubcategoriesLoaded] = useState(false);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const [variations, setVariations] = useState([]);
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [expandedVariation, setExpandedVariation] = useState(null);

  // Working copy of the dealer's hide-list (arrays of numeric IDs per level)
  const [hidden, setHidden] = useState(EMPTY_HIDDEN);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Seed working copy from the dealer's existing settings
  useEffect(() => {
    const h = dealer?.hidden_items || {};
    setHidden({
      subcategories: (h.subcategories || []).map(Number),
      products: (h.products || []).map(Number),
      variationItems: (h.variationItems || []).map(Number),
      detailItems: (h.detailItems || []).map(Number),
      detailDetailItems: (h.detailDetailItems || []).map(Number),
    });
  }, [dealer]);

  // Load subcategories lazily the first time the tab is opened
  const loadSubcategories = useCallback(async () => {
    if (!productApis || subcategoriesLoaded) return;
    setLoadingSubcategories(true);
    try {
      const list = await productApis.getProductSubcategories();
      setSubcategories(list);
      setSubcategoriesLoaded(true);
    } catch (e) {
      console.error('Error loading subcategories:', e);
    } finally {
      setLoadingSubcategories(false);
    }
  }, [productApis, subcategoriesLoaded]);

  useEffect(() => {
    if (activeTab === 'categories') loadSubcategories();
  }, [activeTab, loadSubcategories]);

  // Load ALL products once (no pagination — fetch up to PRODUCTS_TAKE)
  useEffect(() => {
    if (!productApis) return;
    const load = async () => {
      setLoadingProducts(true);
      try {
        const list = await productApis.getStoreProducts({ categoryId: 0, take: PRODUCTS_TAKE });
        setProducts(list);
      } catch (e) {
        console.error('Error loading products:', e);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, [productApis]);

  // Load unique variations lazily the first time the tab is opened
  const loadVariations = useCallback(async () => {
    if (!productApis) return;
    setLoadingVariations(true);
    try {
      const list = await productApis.getStoreVariations();
      setVariations(list);
    } catch (e) {
      console.error('Error loading variations:', e);
    } finally {
      setLoadingVariations(false);
    }
  }, [productApis]);

  useEffect(() => {
    if (activeTab === 'variations') loadVariations();
  }, [activeTab, loadVariations]);

  // ---- hide-list helpers ----
  const isHidden = (level, id) => hidden[level].includes(Number(id));

  const toggleHidden = (level, id) => {
    const numId = Number(id);
    setSaveSuccess(false);
    setHidden((prev) => {
      const exists = prev[level].includes(numId);
      return {
        ...prev,
        [level]: exists ? prev[level].filter((x) => x !== numId) : [...prev[level], numId],
      };
    });
  };

  // Bulk show/hide a set of IDs at a level
  const setManyHidden = (level, ids, makeHidden) => {
    const nums = ids.map(Number);
    setSaveSuccess(false);
    setHidden((prev) => {
      const current = new Set(prev[level]);
      if (makeHidden) nums.forEach((n) => current.add(n));
      else nums.forEach((n) => current.delete(n));
      return { ...prev, [level]: Array.from(current) };
    });
  };

  const handleSave = async () => {
    if (!adminApis || !dealer?.email) {
      setSaveError('Missing dealer information. Please go back and try again.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const result = await adminApis.updateDealerHiddenItems(dealer.email, hidden);
      if (result.success) setSaveSuccess(true);
      else setSaveError(result.error || 'Failed to save visibility settings.');
    } catch (e) {
      console.error('Error saving visibility:', e);
      setSaveError('Network error while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Checkbox where checked = visible to the dealer (unchecked = hidden)
  const VisibilityToggle = ({ level, id, label, sublabel }) => {
    const visible = !isHidden(level, id);
    return (
      <label className="flex items-center gap-2 cursor-pointer select-none py-0.5">
        <input
          type="checkbox"
          checked={visible}
          onChange={() => toggleHidden(level, id)}
          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <span className={`text-sm ${visible ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
          {label}
        </span>
        {sublabel && <span className="text-xs text-gray-400">· {sublabel}</span>}
      </label>
    );
  };

  if (!dealer) {
    return (
      <div className="space-y-6">
        <button
          onClick={onBack}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <FaArrowLeft className="w-5 h-5" />
        </button>
        <p className="text-gray-600">No dealer selected.</p>
      </div>
    );
  }

  const filteredProducts = products.filter((p) =>
    (p.name || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  const hiddenCount =
    hidden.subcategories.length +
    hidden.products.length +
    hidden.variationItems.length +
    hidden.detailItems.length +
    hidden.detailDetailItems.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Visibility</h1>
            <p className="text-gray-600 mt-1">
              {dealer.storeName || dealer.email}
              {dealer.contactPerson ? ` — ${dealer.contactPerson}` : ''}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <FaSave className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Info / status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        Checked items are visible to this dealer. Uncheck anything you don't want them to
        see. Variation choices apply everywhere they're used.
        <span className="ml-1 font-medium">{hiddenCount} item(s) hidden.</span>
      </div>

      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          Visibility settings saved.
        </div>
      )}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          {saveError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('categories')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'categories'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FaThLarge className="w-4 h-4" /> Categories
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'products'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FaBoxOpen className="w-4 h-4" /> Products
        </button>
        <button
          onClick={() => setActiveTab('variations')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'variations'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FaSlidersH className="w-4 h-4" /> Variations
        </button>
      </div>

      {/* Categories tab */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-end gap-2 text-sm mb-4">
            <button
              onClick={() => setManyHidden('subcategories', subcategories.map((s) => s.id), false)}
              className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            >
              Show all
            </button>
            <button
              onClick={() => setManyHidden('subcategories', subcategories.map((s) => s.id), true)}
              className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Hide all
            </button>
          </div>
          {loadingSubcategories ? (
            <p className="text-gray-500">Loading categories...</p>
          ) : subcategories.length === 0 ? (
            <p className="text-gray-500">No categories found.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {subcategories.map((sub) => (
                <div key={sub.id} className="py-2">
                  <VisibilityToggle level="subcategories" id={sub.id} label={sub.category_name} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products tab */}
      {activeTab === 'products' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2 text-sm">
              <button
                onClick={() => setManyHidden('products', filteredProducts.map((p) => p.id), false)}
                className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                Show all
              </button>
              <button
                onClick={() => setManyHidden('products', filteredProducts.map((p) => p.id), true)}
                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Hide all
              </button>
            </div>
          </div>

          {loadingProducts ? (
            <p className="text-gray-500">Loading products...</p>
          ) : filteredProducts.length === 0 ? (
            <p className="text-gray-500">No products found.</p>
          ) : (
            <>
              <p className="text-xs text-gray-400 mb-2">{filteredProducts.length} products</p>
              <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="py-2">
                    <VisibilityToggle
                      level="products"
                      id={product.id}
                      label={product.name}
                      sublabel={product.product_category || product.category_name}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Variations tab */}
      {activeTab === 'variations' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {loadingVariations ? (
            <p className="text-gray-500">Loading variations...</p>
          ) : variations.length === 0 ? (
            <div className="text-gray-500 space-y-2">
              <p>
                No variations found for store{' '}
                <span className="font-mono">{String(adminApis?.storeId)}</span>.
              </p>
              <button
                onClick={loadVariations}
                className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {variations.map((variation) => {
                const expanded = expandedVariation === variation.id;
                const items = variation.variation_items || [];
                return (
                  <div key={variation.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedVariation(expanded ? null : variation.id)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
                    >
                      <span className="text-sm font-semibold text-gray-800">
                        {variation.variation_title}
                        {variation.is_required && <span className="text-red-500 ml-1">*</span>}
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {items.length} option{items.length === 1 ? '' : 's'}
                        </span>
                      </span>
                      {expanded ? (
                        <FaChevronDown className="w-3 h-3 text-gray-500" />
                      ) : (
                        <FaChevronRight className="w-3 h-3 text-gray-500" />
                      )}
                    </button>

                    {expanded && (
                      <div className="p-4 border-t border-gray-200 space-y-2">
                        {items.length === 0 ? (
                          <p className="text-sm text-gray-500">No options.</p>
                        ) : (
                          items.map((item) => (
                            <div key={item.id}>
                              <VisibilityToggle
                                level="variationItems"
                                id={item.id}
                                label={item.variation_name}
                              />
                              {/* Detail items (grouped) */}
                              {(item.variation_item_details || []).map((detail) => (
                                <div key={detail.id} className="pl-6 mt-1 space-y-1">
                                  {(detail.variation_item_detail_items || []).map((di) => (
                                    <div key={di.id}>
                                      <VisibilityToggle
                                        level="detailItems"
                                        id={di.id}
                                        label={di.item_name}
                                        sublabel={detail.variation_detail_title}
                                      />
                                      {/* 3rd-level detail items (grouped) */}
                                      {(di.variation_item_detail_item_details || []).map((dd) => (
                                        <div key={dd.id} className="pl-6 mt-1 space-y-1">
                                          {(dd.variation_item_detail_item_detail_items || []).map((ddi) => (
                                            <VisibilityToggle
                                              key={ddi.id}
                                              level="detailDetailItems"
                                              id={ddi.id}
                                              label={ddi.item_name}
                                              sublabel={dd.variation_detail_title}
                                            />
                                          ))}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageProductVisibility;
